import { Box, Radio, Typography, CircularProgress, Avatar, Stack, Divider, OutlinedInput, Button, Chip, Accordion, AccordionSummary, AccordionDetails, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MathContent from "../../../../../customComponents/MathContent";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useGetQuestionQuery, useAddCommentMutation } from "../../../../../../app/questionApiSlice";
import { selectCurrentUser } from "../../../../auth/authSlice";
import ObjectiveForm from "../NewQuestion/ObjectiveForm";
import SubjectiveForm from "../NewQuestion/SubjectiveForm";
import { stripHtmlTags } from "../../../../../../helpers/htmlToText";
import { useFormik } from "formik";
import * as yup from "yup";

const EditQuestion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const currentUser = useSelector(selectCurrentUser);
  const { data, isLoading, error } = useGetQuestionQuery(id);
  const [addComment, { isLoading: isCommenting }] = useAddCommentMutation();
  const [questionType, setQuestionType] = useState(null);

  const question = data?.question || {};
  const reviews = question.reviews || [];
  const activeReviews = reviews.filter((r) => !r.is_deleted);

  const commentFormik = useFormik({
    initialValues: { body: "" },
    validationSchema: yup.object({
      body: yup.string().required("Comment cannot be empty"),
    }),
    onSubmit: async (values) => {
      try {
        await addComment({
          body: values.body,
          question_id: id,
        }).unwrap();
        commentFormik.resetForm();
      } catch (err) {
        console.error("Error adding comment:", err);
      }
    },
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Set question type once data is loaded
  useEffect(() => {
    if (data?.question) {
      setQuestionType(data.question.bank === "subjective" ? "subjective" : "objective");
    }
  }, [data]);

  const handleChange = (event) => {
    setQuestionType(event.target.value);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error loading question. Please try again.
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 2, cursor: "pointer", color: "primary.safeOnLight" }}
          onClick={() => navigate(-1)}
        >
          Go back
        </Typography>
      </Box>
    );
  }

  if (!data?.question) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Question not found</Typography>
        <Typography
          variant="body2"
          sx={{ mt: 2, cursor: "pointer", color: "primary.safeOnLight" }}
          onClick={() => navigate(-1)}
        >
          Go back
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Comments Section */}
      {activeReviews.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Comments ({activeReviews.length})
          </Typography>

          <Stack spacing={0}>
            {activeReviews
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
              .map((review) => {
                const isCurrentUser = review.user_id === currentUser?.id;
                const isQuestionCreator = review.user_id === question.user_id;
                const displayName = isCurrentUser ? "You" : isQuestionCreator ? "Teacher" : "Reviewer";
                const avatarLetter = isCurrentUser ? "Y" : isQuestionCreator ? "T" : "R";
                const avatarColor = isCurrentUser ? "#2371d1" : isQuestionCreator ? "#27AE60" : "#9e9e9e";
                return (
                  <Box
                    key={review.id}
                    sx={{
                      display: "flex",
                      gap: 2,
                      py: 2,
                      borderBottom: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#333" : "#f0f0f0"}`,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: 14,
                        bgcolor: avatarColor,
                        flexShrink: 0,
                      }}
                    >
                      {avatarLetter}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {displayName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "gray" }}>
                          {formatDate(review.created_at)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, wordBreak: "break-word", color: "text.primary" }}
                      >
                        {stripHtmlTags(review.body || "")}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
          </Stack>

          {/* Comment input */}
          <Box
            component="form"
            onSubmit={commentFormik.handleSubmit}
            sx={{ mt: 3, display: "flex", gap: 2, alignItems: "flex-start" }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 14,
                bgcolor: "#2371d1",
                flexShrink: 0,
                mt: 0.5,
              }}
            >
              You
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <OutlinedInput
                placeholder="Add a comment..."
                fullWidth
                multiline
                minRows={2}
                value={commentFormik.values.body}
                onChange={commentFormik.handleChange}
                name="body"
                sx={{ bgcolor: (theme) => theme.palette.mode === "dark" ? "#2b2b2b" : "#fff" }}
              />
              {commentFormik.errors.body && commentFormik.touched.body && (
                <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5 }}>
                  {commentFormik.errors.body}
                </Typography>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isCommenting || !commentFormik.values.body.trim()}
                  size="small"
                  sx={{ minWidth: 100 }}
                >
                  {isCommenting ? "Sending..." : "Comment"}
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mt: 3 }} />
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 2, md: 4 } }}>
        <Typography sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, fontWeight: 600 }}>
          Edit Question
        </Typography>
        <Chip
          label={questionType === "objective" ? "Objective" : "Subjective"}
          variant="outlined"
          sx={{ fontWeight: 500, textTransform: "capitalize" }}
        />
      </Box>

      <Accordion
        disableGutters
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${isDark ? "#1e3a5f" : "#bfdbfe"}`,
          borderRadius: "10px !important",
          bgcolor: isDark ? "#0d1f33" : "#eff6ff",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: isDark ? "#64b5f6" : "#1d4ed8" }} />}
          sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0 } }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: isDark ? "#64b5f6" : "#1d4ed8" }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? "#64b5f6" : "#1d4ed8" }}>
              How to write math equations
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
            <code style={{ background: isDark ? "#1a2e4a" : "#dbeafe", borderRadius: 4, padding: "1px 5px" }}>$...$</code> for inline or <code style={{ background: isDark ? "#1a2e4a" : "#dbeafe", borderRadius: 4, padding: "1px 5px" }}>$$...$$</code> for block display.
          </Typography>
          <Box
            component="table"
            sx={{ borderCollapse: "collapse", width: "100%", fontSize: "0.8rem" }}
          >
            <tbody>
              {[
                ["Fraction", "$\\frac{a}{b}$"],
                ["Power / Subscript", "$x^{2}$, $x_{1}$"],
                ["Square root", "$\\sqrt{x}$"],
                ["Greek letters", "$\\alpha$, $\\beta$, $\\pi$"],
                ["Trig functions", "$\\sin(x)$, $\\cos(x)$"],
                ["Multiplication dot", "$a \\cdot b$"],
                ["Summation", "$\\sum_{i=1}^{n} i$"],
              ].map(([label, math]) => (
                <Box
                  component="tr"
                  key={label}
                  sx={{ "&:nth-of-type(even) td": { bgcolor: isDark ? "#1a2e4a44" : "#dbeafe33" } }}
                >
                  <Box component="td" sx={{ py: 0.5, pr: 2, color: "text.secondary", whiteSpace: "nowrap" }}>
                    {label}
                  </Box>
                  <Box component="td" sx={{ py: 0.5, pr: 2 }}>
                    <code style={{ background: isDark ? "#1a2a3a" : "#f1f5f9", borderRadius: 4, padding: "1px 5px" }}>{math}</code>
                  </Box>
                  <Box component="td" sx={{ py: 0.5, color: "text.secondary" }}>
                    <MathContent text={math} />
                  </Box>
                </Box>
              ))}
            </tbody>
          </Box>
        </AccordionDetails>
      </Accordion>

      {questionType === "objective" && (
        <ObjectiveForm initialData={data.question} isEditing={true} questionId={id} />
      )}
      {questionType === "subjective" && (
        <SubjectiveForm initialData={data.question} isEditing={true} questionId={id} />
      )}
    </Box>
  );
};

export default EditQuestion;
